import { useState } from 'react';
import { Modal } from '@adiox/ui';
import type { ChartColors } from './chartColors';
import {
  COLOR_KEYS,
  COLOR_LABELS,
  DEFAULT_PROPERTIES,
  normalizeProperties,
  type ChartProperties,
} from './chartProperties';

export interface ChartSettings {
  properties: ChartProperties;
  autoScroll: boolean;
  showTrades: boolean;
  bidAsk: boolean;
}

const label = 'mb-1 block text-xs uppercase tracking-wide text-muted';
const field =
  'w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent num';

/** MT5's chart properties (F8): what the chart shows, its scale and its colours, for this chart only. */
export function ChartPropertiesDialog({
  open,
  title,
  settings,
  theme,
  onSave,
  onClose,
}: {
  open: boolean;
  title: string;
  settings: ChartSettings;
  /** The theme's colours, shown where the chart has none of its own. */
  theme: ChartColors;
  onSave: (settings: ChartSettings) => void;
  onClose: () => void;
}) {
  return (
    <Modal size="md" open={open} title={`Properties: ${title}`} onClose={onClose}>
      {open && <Form settings={settings} theme={theme} onSave={onSave} onClose={onClose} />}
    </Modal>
  );
}

function Form({
  settings,
  theme,
  onSave,
  onClose,
}: {
  settings: ChartSettings;
  theme: ChartColors;
  onSave: (settings: ChartSettings) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'show' | 'scale' | 'colors'>('show');
  const [draft, setDraft] = useState(settings);
  const [min, setMin] = useState(settings.properties.fixedMin?.toString() ?? '');
  const [max, setMax] = useState(settings.properties.fixedMax?.toString() ?? '');
  const p = draft.properties;
  const setProps = (patch: Partial<ChartProperties>) =>
    setDraft({ ...draft, properties: { ...p, ...patch } });

  const fixedProblem =
    p.scale !== 'fixed'
      ? null
      : !Number.isFinite(Number(min)) ||
          !Number.isFinite(Number(max)) ||
          min.trim() === '' ||
          max.trim() === ''
        ? 'Enter the lowest and the highest price of the fixed scale.'
        : Number(min) >= Number(max)
          ? 'The highest price must be above the lowest.'
          : null;

  const themeColor = (key: (typeof COLOR_KEYS)[number]) =>
    ({
      background: theme.panel,
      foreground: theme.muted,
      grid: theme.border,
      bull: theme.up,
      bear: theme.down,
    })[key];

  const toggles: [string, boolean, (on: boolean) => void][] = [
    ['Grid', p.grid, (on) => setProps({ grid: on })],
    ['Volumes', p.volumes, (on) => setProps({ volumes: on })],
    ['Period separators', p.periodSeparators, (on) => setProps({ periodSeparators: on })],
    ['Chart shift', p.chartShift, (on) => setProps({ chartShift: on })],
    ['Auto-scroll', draft.autoScroll, (on) => setDraft({ ...draft, autoScroll: on })],
    ['Trade levels (positions, orders, stops)', p.tradeLevels, (on) => setProps({ tradeLevels: on })],
    [
      'Trade history (entry and exit arrows)',
      draft.showTrades,
      (on) => setDraft({ ...draft, showTrades: on }),
    ],
    ['Bid and ask lines', draft.bidAsk, (on) => setDraft({ ...draft, bidAsk: on })],
  ];

  return (
    <form
      className="flex flex-col gap-3"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (fixedProblem) return;
        const properties = normalizeProperties({
          ...p,
          fixedMin: p.scale === 'fixed' ? Number(min) : null,
          fixedMax: p.scale === 'fixed' ? Number(max) : null,
        });
        onSave({ ...draft, properties });
        onClose();
      }}
    >
      <div role="tablist" aria-label="Chart properties" className="flex gap-1 border-b border-line">
        {(['show', 'scale', 'colors'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`px-3 py-1 text-sm capitalize ${tab === t ? 'border-b-2 border-accent font-semibold' : 'text-muted'}`}
          >
            {t === 'show' ? 'Show' : t === 'scale' ? 'Scale' : 'Colors'}
          </button>
        ))}
      </div>

      {tab === 'show' && (
        <div role="tabpanel" aria-label="Show" className="flex flex-col gap-2">
          {toggles.map(([text, on, set]) => (
            <label key={text} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} />
              {text}
            </label>
          ))}
        </div>
      )}

      {tab === 'scale' && (
        <div role="tabpanel" aria-label="Scale" className="flex flex-col gap-2 text-sm">
          {(
            [
              ['auto', 'Automatic (fits what is on screen)'],
              ['log', 'Logarithmic'],
              ['fixed', 'Fixed range'],
            ] as const
          ).map(([value, text]) => (
            <label key={value} className="flex items-center gap-2">
              <input
                type="radio"
                name="scale"
                checked={p.scale === value}
                onChange={() => setProps({ scale: value })}
              />
              {text}
            </label>
          ))}
          {p.scale === 'fixed' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={label} htmlFor="scale-min">
                  Lowest price
                </label>
                <input
                  id="scale-min"
                  className={field}
                  inputMode="decimal"
                  value={min}
                  onChange={(e) => setMin(e.target.value)}
                />
              </div>
              <div>
                <label className={label} htmlFor="scale-max">
                  Highest price
                </label>
                <input
                  id="scale-max"
                  className={field}
                  inputMode="decimal"
                  value={max}
                  onChange={(e) => setMax(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'colors' && (
        <div role="tabpanel" aria-label="Colors" className="flex flex-col gap-2 text-sm">
          {COLOR_KEYS.map((key) => {
            const own = p.colors[key];
            return (
              <div key={key} className="flex items-center gap-2">
                <span className="min-w-0 flex-1">{COLOR_LABELS[key]}</span>
                <input
                  type="color"
                  aria-label={`${COLOR_LABELS[key]} color`}
                  value={own ?? toHex(themeColor(key))}
                  onChange={(e) => setProps({ colors: { ...p.colors, [key]: e.target.value } })}
                  className="h-7 w-10 cursor-pointer rounded border border-line bg-transparent"
                />
                <button
                  type="button"
                  disabled={!own}
                  className="rounded border border-line px-2 py-0.5 text-xs hover:border-accent disabled:opacity-40"
                  onClick={() => {
                    const { [key]: _gone, ...rest } = p.colors;
                    setProps({ colors: rest });
                  }}
                >
                  Theme
                </button>
              </div>
            );
          })}
          <p className="text-xs text-muted">“Theme” goes back to the colours of the current theme.</p>
        </div>
      )}

      <p role="alert" className="min-h-5 text-sm text-down">
        {fixedProblem}
      </p>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="rounded border border-line px-3 py-1 text-sm hover:border-accent"
          onClick={() => {
            setDraft({
              ...draft,
              properties: DEFAULT_PROPERTIES,
              autoScroll: true,
              showTrades: true,
              bidAsk: true,
            });
            setMin('');
            setMax('');
          }}
        >
          Reset
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-line px-3 py-1 text-sm hover:border-accent"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={fixedProblem !== null}
            className="rounded-md bg-accent px-3 py-1 text-sm font-semibold text-accent-fg disabled:opacity-40"
          >
            OK
          </button>
        </div>
      </div>
    </form>
  );
}

/** A colour input needs #rrggbb; theme colours may be written another way. */
function toHex(color: string): string {
  if (/^#[0-9a-f]{6}$/i.test(color)) return color;
  if (/^#[0-9a-f]{3}$/i.test(color)) return `#${[...color.slice(1)].map((c) => c + c).join('')}`;
  const rgb = color.match(/\d+/g);
  if (rgb && rgb.length >= 3)
    return `#${rgb
      .slice(0, 3)
      .map((n) => Number(n).toString(16).padStart(2, '0'))
      .join('')}`;
  return '#808080';
}
